import some from "lodash/some";
import { FC, Fragment, useContext, useEffect, useRef, useState } from "react";
import { MemberSetModel } from "@app/members";
import CreateProject from "@app/projects/create/createProject";
import { ProjectOverrideMemberSetType } from "@app/projects/enumerators";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

interface CreateMemberTableProps {
  createProjectModel: CreateProject;
  deleteAltMembershipRow: (index: number) => void;
  generateAlternateMemberships: () => void;
  handleAlternativeMemberPopAliasChange: (alias: string, index: number) => void;
  handleAlternativeMemberPopChange: (memberPopId: number, index: number) => void;
  handleNewAlternativeMemberPop: () => void;
  handleMemberPopAliasChange2: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopAliasChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleMemberPopChange2: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleRiskPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isZipProject: boolean;
  projectMemberPopulations: MemberSetModel[];
}

interface MemberPopHandlers {
  defaultOptionCopy: string;
  onPopChange: (e: React.ChangeEvent<HTMLSelectElement>, index?: number) => void;
  onAliasChange: (e: React.ChangeEvent<HTMLInputElement>, index?: number) => void;
}


const CreateMemberTable: FC<CreateMemberTableProps> = (props: CreateMemberTableProps) => {
  const user = useContext(UserContext);
  const allMemberPopOptions = useRef(props.projectMemberPopulations.map((option) => (
    option.hasActivatedVersions ?
      <option key={option.memberSetId} value={option.memberSetId}>{option.resource.name}</option> :
      <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
  ))).current;

  const getMemberPopsInUse = () => {
    return props.createProjectModel.createProjectOverrideMemberSets
      .filter((oms) => oms.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate)
      .map((oms) => oms.memberSetId)
      .concat(props.createProjectModel.memberSetId, props.createProjectModel.memberSetId2)
      .filter((oms) => !!oms);
  }

  const getMemberPopOptions = () => {
    const idsInUse = getMemberPopsInUse();
    return props.projectMemberPopulations.map((option) => (
      option.hasActivatedVersions ?
        <option key={option.memberSetId} value={option.memberSetId} disabled={idsInUse.includes(option.memberSetId)}>{option.resource.name}</option> :
        <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
    ));
  }

  const getShouldDisableAdd = (): boolean => {
    let selectedMembersetsLength = props.createProjectModel.createProjectOverrideMemberSets.filter((oms) => oms.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate).length;
    if (!!props.createProjectModel.memberSetId)
      selectedMembersetsLength += 1;
    if (!!props.createProjectModel.memberSetId2)
      selectedMembersetsLength += 1;

    return selectedMembersetsLength >= allMemberPopOptions.length;
  }

  const [memberPopOptions, setMemberPopOptions] = useState(getMemberPopOptions());
  const [disableAdd, setDisableAdd] = useState(getShouldDisableAdd());

  useEffect(() => {
    setMemberPopOptions(getMemberPopOptions());
    setDisableAdd(getShouldDisableAdd());
  }, [props.createProjectModel.createProjectOverrideMemberSets, props.createProjectModel.memberSetId, props.createProjectModel.memberSetId2]);

  const getRiskAnalysisMemberPopOptions = () => {
    return props.projectMemberPopulations.map((option) => (
      option.hasActivatedVersions ?
        <option key={option.memberSetId} value={option.memberSetId}>{option.resource.name}</option> :
        <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
    ));
  }

  const renderMembershipRow = (type: string, memberPopulation: string, alias: string, memberSetId: number, index: number) => {
    const otherAliases = new Set(props.createProjectModel.createProjectOverrideMemberSets.filter((oms) => oms.memberSetId !== memberSetId).map((oms) => oms.alias));
    return (
      <div className="row mb-3" key={`${index}-${memberPopulation}`}>
        <div className="col-2 d-inline-flex align-items-center label-s">{type}</div>

        <div className="col-4">
          <QESSelect
            className='form-control'
            label="Member Population"
            hideLabel={true}
            testId={`new-project__member-pop-${index}`}
            value={memberPopulation}
            onChange={(e) => memberPopHandlers[type].onPopChange(e, index)}
          >
            <option>{memberPopHandlers[type].defaultOptionCopy}</option>
            {type === ProjectOverrideMemberSetType.Risk ? getRiskAnalysisMemberPopOptions() : memberPopOptions}
          </QESSelect>
        </div>

        <div className="col-3">
          {type !== ProjectOverrideMemberSetType.Risk && (
            <QESTextInput
              testId={`new-project__member-pop-alias-${index}`}
              label="Member Set Alias"
              hideLabel={true}
              onChange={(e) => memberPopHandlers[type].onAliasChange(e, index)}
              value={(memberPopulation === "") ? "" : alias}
              unrequiredValidation={true}
              isValid={!alias || !otherAliases.has(alias)}
              wasFocusedExternally={true}
              disabled={memberPopulation === ""}
            />
          )}
        </div>

        <div className="col-2">
          {type === ProjectOverrideMemberSetType.Alternate && (
            <QESButton
              className="transparent-button-del ps-0"
              qesTestId={`new-project__delete-member-pop-${index}`}
              onClick={() => props.deleteAltMembershipRow(index)}
              qesType="secondary-danger"
              aria-label="Delete Alternate Member Population"
            >
              <QesIcon iconName={QesIcons.TrashV1} isDecorative width="18" />
            </QESButton>
          )}
        </div>
      </div>
    );
  }

  const getRowMemberPopId = (memberSetType: string, index: number): string => {
    let memberPopId = "";
    if (memberSetType === ProjectOverrideMemberSetType.Primary && props.createProjectModel.memberSetId) {
      memberPopId = props.createProjectModel.memberSetId.toString();
    } else if (memberSetType === ProjectOverrideMemberSetType.Secondary && props.createProjectModel.memberSetId2) {
      memberPopId = props.createProjectModel.memberSetId2.toString();
    } else if (memberSetType === ProjectOverrideMemberSetType.Risk && some(user.roles, ((s) => s === Roles.MEMBERSHIP_RISK_USER)) && props.createProjectModel.membershipRiskMemberSetId) {
      memberPopId = props.createProjectModel.membershipRiskMemberSetId.toString();
    } else if (memberSetType === ProjectOverrideMemberSetType.Alternate) {
      memberPopId = props.createProjectModel.createProjectOverrideMemberSets[index].memberSetId?.toString();
    }
    return memberPopId;
  }

  const getRowAlias = (membershipSetType: string, index: number): string => {
    if (membershipSetType === ProjectOverrideMemberSetType.Primary) {
      return props.createProjectModel.memberSetAlias;
    } else if (membershipSetType === ProjectOverrideMemberSetType.Secondary) {
      return props.createProjectModel.memberSetAlias2;
    } else if (membershipSetType === ProjectOverrideMemberSetType.Alternate) {
      return props.createProjectModel.createProjectOverrideMemberSets[index].alias || "";
    }
  }

  const updateRowAlias = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const alias = e.target.value;
    props.handleAlternativeMemberPopAliasChange(alias, index);
  }

  const updateRowId = (e: React.ChangeEvent<HTMLSelectElement>, prevMemberSetId: number) => {
    const memberSetId = +(e.target.value);
    props.handleAlternativeMemberPopChange(memberSetId, prevMemberSetId);
    props.generateAlternateMemberships();
  }

  const memberPopHandlers: { [key: string]: MemberPopHandlers } = {
    [ProjectOverrideMemberSetType.Primary]: {
      defaultOptionCopy: "Select a Member Population",
      onAliasChange: props.handleMemberPopAliasChange,
      onPopChange: props.handleMemberPopChange,
    },
    [ProjectOverrideMemberSetType.Secondary]:
    {
      defaultOptionCopy: "Select a Second Member Population",
      onAliasChange: props.handleMemberPopAliasChange2,
      onPopChange: props.handleMemberPopChange2,
    },
    [ProjectOverrideMemberSetType.Risk]: {
      defaultOptionCopy: "Select a Risk Analysis Member Population",
      onAliasChange: () => null,
      onPopChange: props.handleRiskPopChange,
    },
    [ProjectOverrideMemberSetType.Alternate]: {
      defaultOptionCopy: "Select an Alternate Member Population",
      onAliasChange: updateRowAlias,
      onPopChange: updateRowId,
    }
  };

  return (
    <div className="form-group">
      <Fragment>
        <div className="row">
          <div className="col-2 label-s">Member Population Type</div>
          <div className="col-4 label-s">Member Population</div>
          <div className="col-3 label-s">Alias</div>
        </div>
        {props.createProjectModel.createProjectOverrideMemberSets.map((sr, index) => {
          if (props.isZipProject && sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
            return null;
          }
          const memberPopId = getRowMemberPopId(sr.projectOverrideMemberSetType, index);
          const alias = getRowAlias(sr.projectOverrideMemberSetType, index);
          return renderMembershipRow(sr.projectOverrideMemberSetType, memberPopId, alias, sr.memberSetId, index);
        })}
        <QESButton qesType="secondary" onClick={props.handleNewAlternativeMemberPop} className="col-3 text-start" disabled={disableAdd} qesTestId="new-project__add-alternate-membership">
          <QesIcon iconName={QesIcons.Plus} isDecorative className="align-text-top me-2" /> Add Alternate Member Population
        </QESButton>
      </Fragment>
    </div>
  );
}

export default CreateMemberTable;
